import Immutable from 'immutable';
import React from 'react';

import Validation, { Form } from 'modules/react-validation';

import AppendToBody from 'sharedreact/components/AppendToBody';
import Button from 'sharedreact/components/Button';
import CheckBoxField from 'sharedreact/components/CheckBoxField';
import ParameterComponent from 'sharedreact/components/ParameterComponent';
import ParameterSet, { ParameterGroupProvider } from 'sharedreact/components/ParameterSet';
import Row from 'sharedreact/components/Row';
import Tab from 'sharedreact/components/Tab';
import TextInfoField from 'sharedreact/components/TextInfoField';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import WarningPanel from 'sharedreact/components/WarningPanel';
import askConfirmation from 'sharedreact/components/askConfirmation';
import { translate } from 'sharedreact/utils/LanguageSupport';

import EditLdapConfigDialog from 'workspaces/launcher/components/admin/EditLdapConfigDialog';
import EditOpenIDConfigDialog from 'workspaces/launcher/components/admin/EditOpenIDConfigDialog';
import OpenIDConfigTable from 'workspaces/launcher/components/admin/OpenIDConfigTable';
import IdentityProviders from 'workspaces/launcher/data/IdentityProviders';
import LdapConfig from 'workspaces/launcher/data/LdapConfig';
import OpenIDConfig from 'workspaces/launcher/data/OpenIDConfig';

const _confirmData = {
  complexPasswords: {
    id: 'internal_complexpassword',
    messagePrefix: 'launcher.identityproviders.internal.complexpassword.'
  },
  windowsEnable: {
    id: 'windows_enable',
    messagePrefix: 'launcher.identityproviders.windows.'
  }
};

type Props = {
  identityProviders: IdentityProviders,
  index: number,
  activeTab: number,
  language: string,
  openIDConfigs: Immutable.List<OpenIDConfig>,
  disabled?: boolean,

  onSaveIdentityProviders: (identityProviders: IdentityProviders) => void,
  onSaveOpenIDConfig: (openIDConfig: OpenIDConfig) => void,
  onRemoveOpenIDConfig: (id: string) => void,
  onSaveLdapConfig: (ldapConfig: LdapConfig) => void,
  onLoad: () => void;
};

type State = {
  showOpenID: boolean,
  openIDConfig: OpenIDConfig | null,
  showLdap: boolean,
  ldapConfig: LdapConfig | null
};

class IdentityProvidersTab extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props);

    this.state = {
      showOpenID: false,
      openIDConfig: null,
      showLdap: false,
      ldapConfig: null
    };

    this.handleShowEditOpenID = this.handleShowEditOpenID.bind(this);
    this.handleCloseEditOpenID = this.handleCloseEditOpenID.bind(this);
    this.handleChangeOpenIDConfigValue = this.handleChangeOpenIDConfigValue.bind(this);
    this.handleEnableOpenID = this.handleEnableOpenID.bind(this);
    this.handleRemoveOpenIDConfig = this.handleRemoveOpenIDConfig.bind(this);

    this.handleShowEditLDAP = this.handleShowEditLDAP.bind(this);
    this.handleCloseEditLDAP = this.handleCloseEditLDAP.bind(this);
    this.handleChangeLDAPConfigValue = this.handleChangeLDAPConfigValue.bind(this);
    this.handleEnableLDAP = this.handleEnableLDAP.bind(this);

    this.handleChangeCheckboxValue = this.handleChangeCheckboxValue.bind(this);
  }

  override componentDidMount (): void {
    this.props.onLoad();
  }

  handleShowEditOpenID (openIDConfig: OpenIDConfig | null): void {
    this.setState({showOpenID: true, openIDConfig: openIDConfig || new OpenIDConfig()});
  }

  handleCloseEditOpenID (): void {
    this.setState({showOpenID: false, openIDConfig: null});
  }

  handleChangeOpenIDConfigValue (name: string, value: any): void {
    this.setState((state, props) => {
      if (name === 'clientSecret') {
        return {
          openIDConfig: state.openIDConfig!.set(name, value).set('isClientSecretEdited', true)
        };
      } else if (state.openIDConfig!.has(name)) {
        return {
          openIDConfig: state.openIDConfig!.set(name, value)
        };
      } else {
        throw new Error('IllegalArgumentException: ' + name);
      }
    });
  }

  handleEnableOpenID (openIDConfig: OpenIDConfig): void {
    const { onSaveOpenIDConfig } = this.props;

    askConfirmation(
      'openidconfig_enable',
      translate('launcher.identityproviders.openid.' + (openIDConfig.enabled ? 'enableconfirm' : 'disableconfirm') + '.title'),
      translate('launcher.identityproviders.openid.' + (openIDConfig.enabled ? 'enableconfirm' : 'disableconfirm') + '.message')
    ).then(() => onSaveOpenIDConfig(openIDConfig), () => {});
  }

  handleRemoveOpenIDConfig (id: string): void {
    const { onRemoveOpenIDConfig } = this.props;

    askConfirmation(
      'openidconfig_remove',
      translate('launcher.identityproviders.openid.removeconfirm.title'),
      translate('launcher.identityproviders.openid.removeconfirm.message')
    ).then(() => onRemoveOpenIDConfig(id), () => {});
  }

  handleShowEditLDAP (ldapConfig: LdapConfig): void {
    let ldapConfigToEdit = ldapConfig || new LdapConfig();
    if (ldapConfigToEdit.isNew()) {
      ldapConfigToEdit = ldapConfigToEdit.set('enabled', true);
    }

    this.setState({showLdap: true, ldapConfig: ldapConfigToEdit});
  }

  handleCloseEditLDAP (): void {
    this.setState({showLdap: false, ldapConfig: null});
  }

  handleChangeLDAPConfigValue (name:string, value: any): void {
    this.setState((state, props) => {
      if (name === 'password') {
        return {
          ldapConfig: state.ldapConfig!.set(name, value).set('isPasswordEdited', true)
        };
      } else if (state.ldapConfig!.has(name)) {
        return {
          ldapConfig: state.ldapConfig!.set(name, value)
        };
      } else {
        throw new Error('IllegalArgumentException: ' + name);
      }
    });
  }

  handleEnableLDAP (ldapConfig: LdapConfig): void {
    const { onSaveLdapConfig } = this.props;

    askConfirmation(
      'ldapconfig_enable',
      translate('launcher.identityproviders.ldap.' + (ldapConfig.enabled ? 'enableconfirm' : 'disableconfirm') + '.title'),
      translate('launcher.identityproviders.ldap.' + (ldapConfig.enabled ? 'enableconfirm' : 'disableconfirm') + '.message')
    ).then(() => onSaveLdapConfig(ldapConfig), () => {});
  }

  renderOpenID () {
    const { disabled, openIDConfigs, identityProviders } = this.props;

    return (
      <Row>
        <ParameterSet useProvider={false} title={translate('launcher.identityproviders.oidc')}>
          <TextInfoField id='ae_check_authentication_oidc_info'
            label=''
            value={translate('launcher.identityproviders.oidc.info')}
          />
          <ParameterComponent
            label=''>
            <OpenIDConfigTable
              openIDConfigs={openIDConfigs}
              aesaasLicense={identityProviders.aesaasLicense}
              onEdit={this.handleShowEditOpenID}
              onEnable={this.handleEnableOpenID}
              onRemove={this.handleRemoveOpenIDConfig}
            />
            <Button id='ae_severadmin_oidc_add'
              type='btn-primary'
              className='webappslauncher-openidbutton'
              disabled={disabled || openIDConfigs.size >= 3}
              onClick={() => this.handleShowEditOpenID(null)}>
              {translate('launcher.identityproviders.oidc.button.add')}
            </Button>
          </ParameterComponent>
        </ParameterSet>
      </Row>
    );
  }

  renderLDAPSettings (ldapConfig: LdapConfig) {
    return (
      <React.Fragment>
        <TextInfoField id='ae_text_identityproviders_ldap_server'
          value={ldapConfig.server}
          label={translate('launcher.identityproviders.ldap.server')} />
        <TextInfoField id='ae_text_identityproviders_ldap_info_systemuser'
          value={ldapConfig.systemUser}
          label={translate('launcher.identityproviders.ldap.systemuser')} />
        <TextInfoField id='ae_text_authentication_ldap_info_udn'
          label={translate('launcher.identityproviders.ldap.udn')}
          value={ldapConfig.udn} />
        <TextInfoField id='ae_text_authentication_ldap_groupmembership'
          label={translate('launcher.identityproviders.ldap.groupmembership')}
          value={ldapConfig.groupMembership} />
        <CheckBoxField id='ae_check_authentication_ldap_info_ldaps'
          name='ldaps'
          label={translate('launcher.identityproviders.ldap.ldaps')}
          value={ldapConfig.ldaps}
          disabled
          onChangeValue={() => {}} />
      </React.Fragment>
    );
  }

  renderLDAP () {
    const { identityProviders } = this.props;

    const hasLdapConfig: boolean = identityProviders.ldapConfig.server !== '';

    if (!identityProviders.aesaasLicense) {
      return (
        <Row useAlternateColor>
          <ParameterSet useProvider={false} title={translate('launcher.identityproviders.ldap')}>
            <CheckBoxField id='ae_check_identityproviders_ldap_info_enable'
              name='enabled'
              labelClassName='webappslauncher-labelminwidth200'
              label={translate('launcher.identityproviders.ldap.enable')}
              value={identityProviders.ldapConfig.enabled}
              disabled={!hasLdapConfig}
              onChangeValue={(name, value) => this.handleEnableLDAP(identityProviders.ldapConfig.set('enabled', value))} />
            {identityProviders.ldapConfig.enabled
              ? this.renderLDAPSettings(identityProviders.ldapConfig)
              : null
            }
            <ParameterComponent
              label=''>
              <Button id='ae_button_identityproviders_ldap_info_configure'
                type='btn-primary'
                onClick={() => this.handleShowEditLDAP(identityProviders.ldapConfig)}>
                {translate('launcher.identityproviders.ldap.button.configure')}
              </Button>
            </ParameterComponent>
          </ParameterSet>
        </Row>
      );
    } else {
      return null;
    }
  }

  handleChangeCheckboxValue (name: string, value: boolean): void {
   const { identityProviders, onSaveIdentityProviders } = this.props;

    if (identityProviders.has(name)) {
      const newIdentityProviders = identityProviders.set(name, value);
  
      const confirmInfo: {
        id: string,
        messagePrefix: string,
      } | undefined = name === 'complexPasswords' || name === 'windowsEnable' ? _confirmData[name] : undefined;
  
      if (confirmInfo != null) {
        askConfirmation(
          confirmInfo.id,
          translate(confirmInfo.messagePrefix + (value ? 'enable' : 'disable') + '.title'),
          translate(confirmInfo.messagePrefix + (value ? 'enable' : 'disable') + '.message')
        ).then(() => onSaveIdentityProviders(newIdentityProviders), () => {});
      } else {
        onSaveIdentityProviders(newIdentityProviders);
      }
    } else {
      throw new Error('IllegalArgumentException: ' + name);
    }
  }

  renderInternal () {
    const { disabled, identityProviders } = this.props;

    return (
      <Row useAlternateColor={identityProviders.aesaasLicense}>
        <ParameterSet useProvider={false} title={translate('launcher.identityproviders.internal')}>
          <TextInfoField id='ae_check_authentication_internal_info'
            label=''
            value={translate('launcher.identityproviders.internal.info')}
          />
          <CheckBoxField id='ae_check_authentication_internal_complexpasswords'
            name='complexPasswords'
            label={translate('launcher.identityproviders.internal.complexpasswords')}
            value={identityProviders.complexPasswords}
            disabled={identityProviders.aesaasLicense || disabled}
            onChangeValue={this.handleChangeCheckboxValue}
          />
          {identityProviders.aesaasLicense && <TextInfoField id='ae_check_authentication_internal_complexpasswordsaesaas'
            label=''
            value={translate('launcher.identityproviders.internal.complexpasswordsaesaas')} />
            }
        </ParameterSet>
      </Row>
    );
  }

  renderWindows () {
    const { disabled, identityProviders } = this.props;

    if (!identityProviders.aesaasLicense) {
      return (
        <Row useAlternateColor>
          <ParameterSet useProvider={false} title={translate('launcher.identityproviders.windows')}>
            <CheckBoxField id='ae_check_authentication_windows_enable'
              name='windowsEnable'
              label={translate('launcher.identityproviders.windows.enable')}
              value={identityProviders.windowsEnable}
              onChangeValue={this.handleChangeCheckboxValue}
              disabled={disabled}
            />
            <ParameterComponent label=' '>
              <div className='esko_warningline'>
                <WarningPanel text={translate('launcher.identityproviders.windows.obsolete')} />
              </div>
            </ParameterComponent>
          </ParameterSet>
        </Row>
      );
    } else {
      return null;
    }
  }

  override render (): React.ReactNode {
    const {
      index, activeTab,
      onSaveOpenIDConfig,
      onSaveLdapConfig,
      language
      } = this.props;

    if (index === activeTab) {
      return (
        <Tab index={index} activeIndex={activeTab}>
          <AppendToBody>
            <EditOpenIDConfigDialog show={this.state.showOpenID}
              hideValidationPopups={!this.state.showOpenID}
              language={language}
              openIDConfig={this.state.openIDConfig}
              onChangeValue={this.handleChangeOpenIDConfigValue}
              onClose={this.handleCloseEditOpenID}
              onSave={onSaveOpenIDConfig}
            />
            <EditLdapConfigDialog show={this.state.showLdap}
              hideValidationPopups={!this.state.showLdap}
              language={language}
              ldapConfig={this.state.ldapConfig}
              onChangeValue={this.handleChangeLDAPConfigValue}
              onClose={this.handleCloseEditLDAP}
              onSave={onSaveLdapConfig}
            />
          </AppendToBody>
          <Toolbar>
            <ToolbarGroup>
              <ToolbarTitle title={translate('launcher.identityproviders.title')} />
            </ToolbarGroup>
          </Toolbar>
          <div className='webappslauncher-scroll-container'>
            <Validation.components.Form
              className='webappslauncher-stretch-container'>
              <ParameterGroupProvider>
                {this.renderOpenID()}
                {this.renderLDAP()}
                {this.renderInternal()}
                {this.renderWindows()}
              </ParameterGroupProvider>
            </Validation.components.Form>
          </div>
        </Tab>
      );
    } else {
      return null;
    }
  }
}

export default IdentityProvidersTab;
